import cx from 'classnames';
import { useCallback } from 'react';
import type { FunctionComponent } from 'react';

import BoardsIcon from '@atlaskit/icon/core/boards';
import { Analytics } from '@trello/atlassian-analytics';
import { intl } from '@trello/i18n';
import { getScreenFromUrl } from '@trello/marketing-screens';
import {
  useIsBoardPanelOpen,
  useIsInboxPanelOpen,
  useIsPlannerPanelOpen,
} from '@trello/split-screen';
import { getTestId, type BoardSwitcherTestIds } from '@trello/test-ids';

import { boardSwitcherSharedState } from 'app/src/components/BoardSwitcher/boardSwitcherSharedState';
import { IslandNavButton } from 'app/src/components/IslandNav';
import { LazySpotlightPulse } from 'app/src/components/Onboarding';
import { useBoardSwitcherButtonPulseAndSpotlight } from 'app/src/components/PersonalProductivityOnboarding/useBoardSwitcherButtonPulseAndSpotlight';
import { ShortcutTooltip } from 'app/src/components/ShortcutTooltip';

import styles from './BoardSwitcherButton.less';

interface BoardSwitcherButtonProps {
  showLabels?: boolean;
}

export const BoardSwitcherButton: FunctionComponent<
  BoardSwitcherButtonProps
> = ({ showLabels = false }) => {
  const isInboxOpen = useIsInboxPanelOpen();
  const isPlannerOpen = useIsPlannerPanelOpen();
  const isBoardOpen = useIsBoardPanelOpen();

  const { shouldShowSpotlight } = useBoardSwitcherButtonPulseAndSpotlight();

  const onClickHandler = useCallback(async () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'boardSwitcherButton',
      source: getScreenFromUrl(),
      attributes: {
        isInboxOpen,
        isPlannerOpen,
        isBoardOpen,
      },
    });

    boardSwitcherSharedState.setValue({
      isOpen: true,
    });
  }, [isBoardOpen, isInboxOpen, isPlannerOpen]);

  const title = intl.formatMessage({
    id: 'templates.split_screen.switch-boards',
    defaultMessage: 'Switch boards',
    description: 'Switch boards',
  });

  const button = (
    <ShortcutTooltip shortcutText={title} shortcutKey="b" position="top">
      <IslandNavButton
        icon={<BoardsIcon label={showLabels ? '' : title} />}
        onClick={onClickHandler}
        testId={getTestId<BoardSwitcherTestIds>(
          'panel-nav-board-switcher-button',
        )}
        showLabels={showLabels}
        label={title}
      />
    </ShortcutTooltip>
  );

  return (
    <div
      className={cx(styles.boardSwitcherButton, {
        [styles.withLabel]: showLabels,
      })}
    >
      {shouldShowSpotlight ? (
        <LazySpotlightPulse radius={3}>{button}</LazySpotlightPulse>
      ) : (
        button
      )}
    </div>
  );
};
